/* eslint-disable @typescript-eslint/no-unused-vars */

import PropTypes from 'prop-types';
import { memo, useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { animated, useTransition } from 'react-spring';
import styled, { css, useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { useMutationObserver } from '@hivebrite/react-hooks';

import EditItem from 'hb-react/frontoffice/apps/LiveFeed/components/Feed/EditItem';
import {
  loadImages,
  mutationObserver,
  editItems,
  getIsAdmin,
  getItemTopic,
} from 'hb-react/frontoffice/apps/LiveFeed/components/Feed/utils';
import * as Landings from 'hb-react/frontoffice/apps/LiveFeed/components/Landings';
import {
  END_OF_FEED_TYPE,
  END_OF_FEED_SESSION_STORAGE_KEY,
  SUGGESTED_GROUPS,
} from 'hb-react/frontoffice/apps/LiveFeed/components/Landings/EndOfFeed/constants';
import LandingHeader from 'hb-react/frontoffice/apps/LiveFeed/components/Landings/Header';
import PostApi2PostType from 'hb-react/frontoffice/apps/LiveFeed/constants/PostApi2PostType';
import { LandingProvider } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';
import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import { getActionItems } from 'hb-react/frontoffice/apps/LiveFeed/utils/actionItems';
import { getItemsDedicatedUrl } from 'hb-react/frontoffice/apps/LiveFeed/utils/getItemsDedicatedUrl';
import { getShareableData } from 'hb-react/frontoffice/apps/LiveFeed/utils/getShareableData';
import { toggleTopPosts } from 'hb-react/frontoffice/apps/LiveFeed/utils/toggleTopPosts';
import SuggestedGroups from 'hb-react/frontoffice/apps/LiveFeedMyGroups/components/SuggestedGroups';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { blockUser, unblockUser } from 'hb-react/frontoffice/utils/blockUser';
import { useTogglePostLike } from 'hb-react/shared/apps/News/NewsPublished/hooks/useTogglePostLike';
import { MIME_TYPE_VIDEOS } from 'hb-react/shared/constants/mimeTypeVideos';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useNavigatorShare from 'hb-react/shared/hooks/useNavigatorShare';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';
import { getItem, setItem } from 'hb-react/shared/utils/sessionStorage';
import { completeName, fullName } from 'helpers/name';

import { actionItemsIcons } from './actionItemsIcons';

const getCategories = (categories, tags, networkCategories) => {
  let items = [];

  if (categories && categories.length > 0) {
    items = categories;
  } else if (tags && tags.length > 0) {
    items = tags;
  } else if (networkCategories && networkCategories.length > 0) {
    items = networkCategories;
  }

  return items;
};

const getSharedItemProps = ({
  attached_images: attachedImages,
  categories,
  comments,
  created_at: createdAt,
  embed,
  id,
  labelledById,
  last_contributors: lastContributors,
  likes,
  pinned,
  published_at: publishedAt,
  // Come from network event, to remove after API harmonization (use only categories)
  network_categories: networkCategories,
  // Come from topic post
  tags,
  total_comments: totalComments,
  uploads,
  cancelled = false,
}) => ({
  attachedFiles: uploads,
  attachedImages:
    attachedImages?.filter(
      ({ mime_type: mimeType }) => !MIME_TYPE_VIDEOS.includes(mimeType),
    ) || [],
  attachedVideos:
    attachedImages?.filter(({ mime_type: mimeType }) =>
      MIME_TYPE_VIDEOS.includes(mimeType),
    ) || [],
  categories: getCategories(categories, tags, networkCategories),
  comments,
  createdAt,
  embed,
  id,
  labelledById,
  lastContributors,
  likes,
  pinned,
  publishedAt,
  totalComments,
  cancelled,
});
const getOwner = ({ owner, link = false, linkIsExternal = false }) => ({
  id: owner?.id,
  link: owner?.id && link ? Routes.user_path(owner.id) : null,
  name: fullName(owner) || owner?.name,
  pictureUrl: owner?.photo_thumb_url,
  linkIsExternal,
  newPhoto: owner?.new_photo?.thumbnail_url,
});

const Landing = ({ type, hiddenUserIds, ...otherProps }) => {
  // If currentUser just blocks another user publications
  const { currentUser } = useDefaultAppContexts();
  const canHideUser = !!currentUser?.test_account;

  if (canHideUser && hiddenUserIds.includes(otherProps?.item?.user?.id)) {
    return <Blocked {...otherProps} />;
  }

  switch (type) {
    case 'current':
      return <CheckIn {...otherProps} />;

    case 'trip':
      return <Trip {...otherProps} />;

    case 'posts_feed_post':
    case 'posts_topic_post':
      return <UserPost {...otherProps} />;

    case 'communications_post_communication':
    case 'communications_news':
      return <NetworkNews {...otherProps} />;

    case 'event':
      return <UserEvent {...otherProps} />;

    case 'network_events_event':
      return <NetworkEvent {...otherProps} />;

    case 'business_opportunities_request':
      return <Opportunity {...otherProps} />;

    case 'surveys_form':
      return <Survey {...otherProps} />;

    case 'ventures_venture':
      return <Venture {...otherProps} />;

    case 'tweet':
      return <Tweet {...otherProps} />;

    case 'user':
      return <User {...otherProps} />;

    case END_OF_FEED_TYPE:
      return <EndOfFeed {...otherProps} />;

    case 'discussion':
      return <ForumDiscussion {...otherProps} />;

    default:
      return <div>{type}</div>;
  }
};

const Blocked = ({
  item: { user, ...restItem },
  handleShowUser,
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    handleShowUser={handleShowUser}
    owner={getOwner({ owner: user })}
    type="blocked"
    {...otherProps}
  >
    <Landings.Blocked />
  </LandingProvider>
);

const CheckIn = ({
  item: { content_html: contentHtml, locations, user, type, ...restItem },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    content={contentHtml}
    location={locations?.[0]}
    originalType={type}
    owner={getOwner({ owner: user, link: true })}
    type="check-in"
    {...otherProps}
  >
    <Landings.CheckIn />
  </LandingProvider>
);

const Trip = ({
  item: {
    content,
    departure_location: departureLocation,
    end_date: endDate,
    locations,
    start_date: startDate,
    transportation,
    user,
    timezone,
    type,
    created_at: createdAt,
    ...restItem
  },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    // When you create new Trip, API return locations { ... } and not locations [{ ... }]
    arrivalLocation={locations?.[0] ?? locations}
    content={content}
    departureLocation={departureLocation}
    endDate={endDate}
    originalType={type}
    owner={getOwner({ owner: user, link: true })}
    startDate={startDate}
    timezone={timezone}
    transportation={transportation}
    type="trip"
    publishedAt={createdAt}
    {...otherProps}
  >
    <Landings.Trip />
  </LandingProvider>
);

const UserPost = ({
  item: {
    content_html: contentHtml,
    user,
    topic,
    type,
    from_record: fromRecord,
    ...restItem
  },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    content={contentHtml}
    originalType={type}
    owner={getOwner({ owner: user, link: true })}
    topic={topic}
    type="user-post"
    fromRecord={fromRecord}
    {...otherProps}
  >
    <Landings.UserPost />
  </LandingProvider>
);

const NetworkNews = ({
  item: {
    attachments,
    author,
    content,
    cover_picture: coverPicture,
    external_url: externalUrl,
    feedable_at: feedableAt,
    published_at: publishedAt,
    startDate,
    subject,
    type,
    ...restItem
  },
  onTogglePostLike,
  ...otherProps
}) => {
  const { togglePostLike } = useTogglePostLike({
    toggleLikeHandler: onTogglePostLike,
  });

  return (
    <LandingProvider
      {...getSharedItemProps(restItem)}
      attachments={attachments}
      coverPicture={coverPicture}
      externalUrl={externalUrl}
      onTogglePostLike={togglePostLike}
      originalType={type}
      owner={getOwner({ owner: author })}
      postContent={content}
      publishedAt={feedableAt || publishedAt}
      startDate={startDate}
      title={subject}
      type="network-post"
      {...otherProps}
    >
      <Landings.NetworkNews />
    </LandingProvider>
  );
};

const UserEvent = ({
  item: {
    attendees,
    html_description: htmlDescription,
    locations,
    logo,
    logo_url: logoUrl,
    start_date: startDate,
    timezone,
    title,
    total_attendees: totalAttendees,
    type,
    user,
    ...restItem
  },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    attendees={attendees}
    content={htmlDescription}
    location={locations?.[0] ?? locations} // locations might come as an array or a single object
    logo={logo}
    logoUrl={logoUrl}
    originalType={type}
    owner={getOwner({ owner: user, link: true })}
    startDate={startDate}
    timezone={timezone}
    title={title}
    type="user-event"
    {...otherProps}
  >
    <Landings.UserEvent />
  </LandingProvider>
);

const NetworkEvent = ({
  item: {
    attendees,
    cover_picture: coverPicture,
    html_description: htmlDescription,
    locations,
    logo,
    logo_url: logoUrl,
    start_date: startDate,
    feedable_at: feedableAt,
    published_at: publishedAt,
    organizer,
    timezone,
    participant_display_threshold: participantDisplayThreshold,
    total_attendees: totalAttendees,
    total_external_attendees: totalExternalAttendees,
    total_visible_attendees: totalVisibleAttendees,
    total_visible_yes_responses: totalVisibleYesResponses,
    title,
    type,
    ...restItem
  },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    attendees={attendees}
    coverPicture={coverPicture}
    participantDisplayThreshold={participantDisplayThreshold}
    totalAttendees={totalAttendees}
    totalExternalAttendees={totalExternalAttendees}
    totalVisibleAttendees={totalVisibleAttendees}
    totalVisibleYesResponses={totalVisibleYesResponses}
    location={locations?.[0]}
    logo={logo}
    logoUrl={logoUrl}
    originalType={type}
    owner={getOwner({ owner: organizer })}
    startDate={startDate}
    publishedAt={feedableAt || publishedAt}
    timezone={timezone}
    title={title}
    type="network-event"
    {...otherProps}
  >
    <Landings.NetworkEvent />
  </LandingProvider>
);

const Opportunity = ({
  item: {
    company_name: companyName,
    contract_type_name: contractTypeName,
    currency,
    daily_salary: dailySalary,
    duration,
    experience_type_name: experienceTypeName,
    locations,
    logo_thumb_url: logoUrl,
    monthly_salary: monthlySalary,
    start_date: startDate,
    timezone,
    title,
    type,
    user,
    yearly_salary: yearlySalary,
    ...restItem
  },
  currentNetwork,
  ...otherProps
}) => {
  const {
    id: networkId,
    title: networkTitle,
    avatar_medium_url: networkAvatarUrl,
  } = currentNetwork;

  return (
    <LandingProvider
      {...getSharedItemProps(restItem)}
      companyName={companyName}
      contractTypeName={contractTypeName}
      currency={currency}
      dailySalary={dailySalary}
      duration={duration}
      experienceTypeName={experienceTypeName}
      location={locations?.[0]}
      logoUrl={logoUrl}
      originalType={type}
      owner={{
        ...getOwner({ owner: user }),
        id: user?.id || networkId,
        name: user?.name || networkTitle,
        pictureUrl: user?.photo_thumb_url || networkAvatarUrl,
        link: user?.id && Routes.user_path(user.id),
      }}
      monthlySalary={monthlySalary}
      startDate={startDate}
      timezone={timezone}
      title={title}
      type="opportunity"
      yearlySalary={yearlySalary}
      {...otherProps}
    >
      <Landings.Opportunity />
    </LandingProvider>
  );
};

const Survey = ({
  item: { description, title, ...restItem },
  currentNetwork,
  // Exclude handleShare to not pass it further
  handleShare,
  ...otherProps
}) => {
  const {
    id: networkId,
    title: networkTitle,
    avatar_medium_url: networkAvatarUrl,
  } = currentNetwork;

  return (
    <LandingProvider
      {...getSharedItemProps(restItem)}
      owner={{
        id: networkId,
        link: null,
        name: networkTitle,
        pictureUrl: networkAvatarUrl,
      }}
      surveyContent={description}
      title={title}
      type="survey"
      {...otherProps}
    >
      <Landings.Survey />
    </LandingProvider>
  );
};

const Venture = ({
  item: {
    company_name: companyName,
    description,
    high_level_pitch: highLevelPitch,
    locations,
    logo_thumb_url: logoUrl,
    title,
    type,
    user,
    ...restItem
  },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    location={locations?.[0]}
    logoUrl={logoUrl}
    originalType={type}
    owner={getOwner({ owner: user, link: true })}
    title={companyName}
    type="venture"
    ventureContent={highLevelPitch}
    {...otherProps}
  >
    <Landings.Venture />
  </LandingProvider>
);

const Tweet = ({
  item: {
    account_name: accountName,
    content,
    id,
    uploads,
    user_name: userName,
    photo_url: photoUrl,
    twitter_url: twitterUrl,
    ...restItem
  },
  // Exclude handleShare to not pass it further
  handleShare,
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    accountName={accountName}
    content={content}
    owner={getOwner({
      owner: {
        id,
        name: `${userName} (@${accountName})`,
        photo_thumb_url: photoUrl,
      },
      link: twitterUrl,
      linkIsExternal: true,
    })}
    type="tweet"
    {...otherProps}
  >
    <Landings.Tweet />
  </LandingProvider>
);

const User = ({
  item: {
    id,
    photo_thumb_url: photoUrl,
    confirmed_at: confirmedAt,
    published_at: publishedAt,
    type,
    ...restItem
  },
  // Exclude handleShare to not pass it further
  handleShare,
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    id={id}
    originalType={type}
    owner={{
      id,
      link: Routes.user_path(id),
      name: completeName(restItem),
      pictureUrl: photoUrl,
    }}
    type="user"
    publishedAt={confirmedAt || publishedAt}
    {...otherProps}
  >
    <Landings.User />
  </LandingProvider>
);

/**
 * Note: The End of Feed component has a transition animation that should be displayed
 * only once. From the second time the component should be displayed without animation.
 */
const EndOfFeed = ({ item: { id, type } }) => {
  const endOfFeedSessionStorage = getItem({
    key: END_OF_FEED_SESSION_STORAGE_KEY,
  });
  const isDisplayedEndOfFeed = endOfFeedSessionStorage === id;

  /**
   * We store the pseudo id (id comes from the backend side) in order to be used
   * to check if there is a new End of Feed item.
   */
  const onRestCallback = () => {
    if (!isDisplayedEndOfFeed) {
      setItem({
        key: END_OF_FEED_SESSION_STORAGE_KEY,
        value: id,
      });
    }
  };

  const activeTransition = useActiveTransition({
    delay: 3e3,
    isActive: !isDisplayedEndOfFeed,
    onRestCallback,
  });

  return (
    <LandingProvider hasHeader={false} type={type}>
      {!isDisplayedEndOfFeed &&
        activeTransition(
          (styles, item) =>
            item && (
              <animated.div
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  bottom: 0;
                  right: 0;
                  pointer-events: none;
                  z-index: 1;
                `}
                style={styles}
              />
            ),
        )}
      <Landings.EndOfFeed />
    </LandingProvider>
  );
};

const ForumDiscussion = ({
  item: {
    type,
    cover_picture: coverPicture,
    title,
    content,
    user,
    created_at: createdAt,
    ...restItem
  },
  ...otherProps
}) => (
  <LandingProvider
    {...getSharedItemProps(restItem)}
    type={type}
    coverPicture={coverPicture}
    title={title}
    content={content}
    owner={getOwner({
      owner: user,
      link: true,
    })}
    {...otherProps}
  >
    <Landings.ForumDiscussion />
  </LandingProvider>
);

/**
 * Internal hook to customize the useTransition animation.
 *
 * @param {number} delay - to hide border after X seconds (5 seconds by default)
 * @param {boolean} isActive - to active the animation
 * @param {function} onRestCallback - to execute a callback function after the animation ends
 */
const useActiveTransition = ({ delay = 5e3, isActive, onRestCallback }) => {
  const theme = useTheme();

  return useTransition(isActive, {
    reset: true,
    from: {
      opacity: 0,
      border: `2px solid ${theme.colors.primary}`,
    },
    enter: () => async (next) => {
      await next({
        opacity: 1,
      });

      await next({
        opacity: 0,
        delay,
      });
    },
    onRest: () => {
      if (onRestCallback) {
        onRestCallback();
      }
    },
  });
};

const customHandleShare = (flag, handleShare) =>
  flag ? handleShare : undefined;

const Item = memo(
  ({
    displayCommentsByDefault,
    hiddenUserIds,
    isActive,
    isRowVisibleOrOutOfView,
    item,
    itemBeingDeletedId,
    // To remove with `flag-ujb-lf-pagination` cleanup and all usage from everywhere.
    measure,
    onDeleteItem,
    onDeleteVirtualItem,
    onReportContent,
    onSetHiddenUserIds,
    onTogglePostLike,
    onUpdateItem,
    // For now based on LiveFeed type, should be based on item itself
    parentType,
    ...otherProps
  }) => {
    const { currentNetwork, currentSession, currentUser, currentUrl } =
      useDefaultAppContexts();
    const { headerHeight } = useUIContext();
    const {
      config: { type: configType, topic, showTypeHeader },
      amplitudeConfig,
      toggleItemComments,
    } = useLiveFeedContext();
    const navigate = useNavigate();

    const { flagCurrentSessionContext } = useFeatureFlag();
    const amplitude = amplitudeLogger(amplitudeConfig);

    const [isEditing, setIsEditing] = useState(false);

    const labelledById = useMemo(() => uuid(), []);
    const itemTopic = useMemo(
      () => getItemTopic({ configType, topic, item }),
      [configType, topic, item],
    );

    const nodeRef = useRef();

    const postType = PostApi2PostType[item.type];

    const handleLoadImages = useCallback(
      () => loadImages({ nodeRef, measure }),
      [measure],
    );
    const handleMutationObserver = useCallback(
      () => mutationObserver({ nodeRef, measure, handleLoadImages }),
      [measure, handleLoadImages],
    );
    const handleOpenEditModal = useCallback(() => setIsEditing(true), []);
    const handleCloseEditModal = useCallback(() => setIsEditing(false), []);
    const handleEditItem = useCallback(
      () =>
        editItems({
          item,
          postType,
          itemTopic,
          navigate,
          currentUser,
          handleOpenEditModal,
        }),
      [item, postType, itemTopic, navigate, currentUser, handleOpenEditModal],
    );
    const handleDeleteItem = useCallback(() => {
      onDeleteItem({
        postId: item.id,
        type: item.type,
        topicId: item.topic_id,
      });
    }, [item, onDeleteItem]);

    const handleDeleteSuggestedGroups = useCallback(() => {
      onDeleteVirtualItem({
        type: SUGGESTED_GROUPS,
      });
    }, [onDeleteVirtualItem]);

    const handleUpdateItemContent = useCallback(
      (props) => {
        onUpdateItem(props);

        setTimeout(() => {
          // Force measure when images and/or files are loaded or removed
          handleMutationObserver();
        }, 250);
      },
      [onUpdateItem, handleMutationObserver],
    );

    const handleUpdateItem = useCallback(
      (itemUpdated) => {
        handleCloseEditModal();

        handleUpdateItemContent({ newItem: itemUpdated });

        // Don't update anymore PostBox
        return false;
      },
      [handleCloseEditModal, handleUpdateItemContent],
    );

    const handleHideUser = useCallback(() => {
      const hiddenUserIds = blockUser(item?.user?.id);

      onSetHiddenUserIds(hiddenUserIds);
    }, [onSetHiddenUserIds, item]);

    const handleShowUser = useCallback(() => {
      const remainingHiddenUserIds = unblockUser(item?.user?.id);

      onSetHiddenUserIds(remainingHiddenUserIds);
    }, [onSetHiddenUserIds, item]);

    const handleReportContent = useCallback(() => {
      onReportContent(item);
    }, [item, onReportContent]);

    const handleToggleTopPosts = useCallback(async () => {
      const newTopPost = await toggleTopPosts({
        type: item.type,
        itemId: item.id,
        userId: item.user?.id,
      });

      const newItem = {
        ...item,
        ...newTopPost,
      };

      onUpdateItem({ newItem, triggerTopPost: true });
    }, [item, onUpdateItem]);

    // To remove with `flag-ujb-lf-pagination` cleanup
    const handleToggleComments = useCallback(
      (isExpanded) => {
        toggleItemComments(item.id, item.type, isExpanded);
      },
      [item.id, item.type, toggleItemComments],
    );

    const shareableData = getShareableData({ item, parentType, currentUrl });

    const { handleShare, shareUrl } = useNavigatorShare({
      data: shareableData,
      onShare: () =>
        amplitude.track('LiveFeed|ContentShared', {
          type: item.type,
        }),
    });

    const itemUrl = getItemsDedicatedUrl({
      item,
      liveFeedType: parentType,
      currentUrl,
    });
    const actionsItems = getActionItems({
      currentUser,
      handleDeleteItem,
      handleEditItem,
      handleHideUser,
      handleReportContent,
      handleToggleTopPosts,
      icons: actionItemsIcons,
      isAdmin: getIsAdmin({ flagCurrentSessionContext, currentSession }),
      item,
      itemUrl,
      networkConfig: currentNetwork?.network_config,
      user: item.user,
    });
    const hasSocialOptions = !!item.likes;

    const activeTransition = useActiveTransition({ isActive });

    // Force measure when images are load
    useEffect(() => {
      const handlers = handleLoadImages();

      return handlers;
    }, [handleLoadImages]);

    useEffect(() => {
      const requestID = window.requestAnimationFrame(() => {
        if (isActive && nodeRef.current) {
          const { top } = nodeRef.current.getBoundingClientRect();

          window.scrollTo({
            top: window.scrollY + top - headerHeight * 2,
            behavior: 'smooth',
          });
        }
      });

      return () => {
        window.cancelAnimationFrame(requestID);
      };
      // We don't want to re-run this effect when the headerHeight from the UIContext changes
      // (in the topics it changes when the topic menu becomes sticky because we hide the network menu)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, isActive]);

    useMutationObserver(nodeRef, handleMutationObserver);

    return (
      <>
        {isEditing && (
          <EditItem
            handleCloseEditModal={handleCloseEditModal}
            postType={postType}
            isOpen={isEditing}
            item={item}
            handleUpdateItem={handleUpdateItem}
            topic={itemTopic}
          />
        )}

        {item.type === SUGGESTED_GROUPS ? (
          <WrapperSuggestedGroups ref={nodeRef}>
            <SuggestedGroups
              isInFeed
              onLoadedEmpty={handleDeleteSuggestedGroups}
            />
          </WrapperSuggestedGroups>
        ) : (
          <CardItem
            {...otherProps}
            aria-labelledby={labelledById}
            as="article"
            noBorderRadiusOnSmallScreen
            ref={nodeRef}
          >
            <LandingHeader
              itemType={item.type}
              item={getSharedItemProps(item)}
              showTypeHeader={showTypeHeader}
            />
            {activeTransition(
              (styles, item) =>
                item && (
                  <animated.div
                    css={css`
                      position: absolute;
                      top: 0;
                      left: 0;
                      bottom: 0;
                      right: 0;
                      pointer-events: none;
                      z-index: 1;
                    `}
                    style={styles}
                  />
                ),
            )}
            {itemBeingDeletedId === item.id && <OverlayLoading />}
            <Landing
              actionsItems={actionsItems}
              currentNetwork={currentNetwork}
              displayCommentsByDefault={displayCommentsByDefault}
              handleShowUser={handleShowUser}
              hasSocialOptions={hasSocialOptions}
              hiddenUserIds={hiddenUserIds}
              isActive={isActive}
              isRowVisibleOrOutOfView={isRowVisibleOrOutOfView}
              item={item}
              itemUrl={itemUrl}
              labelledById={labelledById}
              measure={measure}
              onToggleComments={handleToggleComments}
              onTogglePostLike={onTogglePostLike}
              onUpdateItem={handleUpdateItemContent}
              type={item.type}
              handleShare={customHandleShare(handleShare)}
              shareUrl={shareUrl}
            />
          </CardItem>
        )}
      </>
    );
  },
);

Item.propTypes = {
  displayCommentsByDefault: PropTypes.bool,
  hiddenUserIds: PropTypes.array,
  isActive: PropTypes.bool,
  isRowVisibleOrOutOfView: PropTypes.bool,
  item: PropTypes.object,
  itemBeingDeletedId: PropTypes.number,
  measure: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onDeleteVirtualItem: PropTypes.func,
  onReportContent: PropTypes.func,
  onSetHiddenUserIds: PropTypes.func,
  onTogglePostLike: PropTypes.func,
  onUpdateItem: PropTypes.func,
  parentType: PropTypes.string,
};

export default Item;

const CardItem = styled(Card)`
  min-height: 120px;
  position: relative;
  padding-top: 16px;
`;
const OverlayLoading = styled(Box)`
  background: ${(props) => props.theme.colors.fog};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 11;
`;

const WrapperSuggestedGroups = styled(Box).attrs(() => ({
  width: '100%',
  height: { _: 235, m: 237, l: 272 },
  pl: { _: 4, m: 0 },
}))``;
